// Composant Menu Catalogue Marketplace - Style ManoMano
// Navigation par rayons et sous-rayons inspirée de ManoMano

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronRight, ChevronLeft, Search, Sparkles,
  Zap, Droplets, Wrench, PaintBucket, Hammer, Trees,
  Bath, Lightbulb, Cog, Home, Car, Thermometer,
  Layers, Building2, PawPrint, Sofa, Menu
} from 'lucide-react';
import { API_URL } from '../../config/api';

// Mapping des icônes par rayon
const RAYON_ICONS = {
  'electricite': Zap,
  'plomberie': Droplets,
  'outillage': Wrench,
  'peinture': PaintBucket,
  'menuiserie': Hammer,
  'jardin': Trees,
  'salle-de-bain': Bath,
  'luminaire': Lightbulb,
  'quincaillerie': Cog,
  'cuisine': Home,
  'automobile': Car,
  'chauffage': Thermometer,
  'carrelage': Layers,
  'construction': Building2,
  'animalerie': PawPrint,
  'mobilier': Sofa
};

// Couleurs par défaut
const RAYON_COLORS = {
  'electricite': '#F59E0B',
  'plomberie': '#3B82F6',
  'outillage': '#EF4444',
  'peinture': '#8B5CF6',
  'menuiserie': '#D97706',
  'jardin': '#22C55E',
  'salle-de-bain': '#06B6D4',
  'luminaire': '#FBBF24',
  'quincaillerie': '#6B7280',
  'cuisine': '#EC4899',
  'automobile': '#1F2937',
  'chauffage': '#F97316',
  'carrelage': '#14B8A6',
  'construction': '#78716C',
  'animalerie': '#A855F7',
  'mobilier': '#0EA5E9'
};

export default function MarketplaceCatalogueMenu({ isOpen, onClose, sidebarWidth }) {
  const navigate = useNavigate();
  const [rayons, setRayons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRayon, setSelectedRayon] = useState(null);
  const [selectedSousRayon, setSelectedSousRayon] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadRayons();
    } else {
      // Reset selections when closing
      setSelectedRayon(null);
      setSelectedSousRayon(null);
    }
  }, [isOpen]);

  const loadRayons = async () => {
    try {
      // Utilise /rayons/full pour avoir les sous-rayons et catégories
      const res = await fetch(`${API_URL}/api/marketplace/rayons/full`);
      if (res.ok) {
        const data = await res.json();
        setRayons(data);
      }
    } catch (e) {
      console.error('Error loading rayons:', e);
      // Fallback avec des rayons par défaut
      setRayons([
        { id: 'rayon-electricite', name: 'Électricité', slug: 'electricite', sous_rayons: [] },
        { id: 'rayon-plomberie', name: 'Plomberie', slug: 'plomberie', sous_rayons: [] },
        { id: 'rayon-outillage', name: 'Outillage', slug: 'outillage', sous_rayons: [] },
        { id: 'rayon-peinture', name: 'Peinture & Droguerie', slug: 'peinture', sous_rayons: [] },
        { id: 'rayon-menuiserie', name: 'Menuiserie', slug: 'menuiserie', sous_rayons: [] },
        { id: 'rayon-jardin', name: 'Jardin & Piscine', slug: 'jardin', sous_rayons: [] },
        { id: 'rayon-salle-de-bain', name: 'Salle de bain & WC', slug: 'salle-de-bain', sous_rayons: [] },
        { id: 'rayon-luminaire', name: 'Luminaire', slug: 'luminaire', sous_rayons: [] },
        { id: 'rayon-quincaillerie', name: 'Quincaillerie', slug: 'quincaillerie', sous_rayons: [] },
        { id: 'rayon-cuisine', name: 'Cuisine', slug: 'cuisine', sous_rayons: [] },
        { id: 'rayon-automobile', name: 'Automobile & Moto', slug: 'automobile', sous_rayons: [] },
        { id: 'rayon-chauffage', name: 'Chauffage & Climatisation', slug: 'chauffage', sous_rayons: [] },
        { id: 'rayon-carrelage', name: 'Carrelage & Sol', slug: 'carrelage', sous_rayons: [] },
        { id: 'rayon-construction', name: 'Construction & Matériaux', slug: 'construction', sous_rayons: [] },
        { id: 'rayon-animalerie', name: 'Animalerie', slug: 'animalerie', sous_rayons: [] },
        { id: 'rayon-mobilier', name: "Mobilier d'intérieur", slug: 'mobilier', sous_rayons: [] }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleRayonClick = (rayon) => {
    if (rayon.sous_rayons && rayon.sous_rayons.length > 0) {
      setSelectedRayon(rayon);
      setSelectedSousRayon(null);
    } else {
      navigate(`/produits?rayon=${rayon.slug}`);
      onClose();
    }
  };

  const handleSousRayonClick = (sousRayon) => {
    if (sousRayon.categories && sousRayon.categories.length > 0) {
      setSelectedSousRayon(sousRayon);
    } else {
      navigate(`/produits?sous_rayon=${sousRayon.slug}`);
      onClose();
    }
  };

  const handleCategoryClick = (category) => {
    navigate(`/produits?category=${category.slug}`);
    onClose();
  };

  const handleBack = () => {
    if (selectedSousRayon) {
      setSelectedSousRayon(null);
    } else if (selectedRayon) {
      setSelectedRayon(null);
    }
  };

  // Titre du header selon le niveau
  const getHeaderTitle = () => {
    if (selectedSousRayon) return selectedSousRayon.name;
    if (selectedRayon) return selectedRayon.name;
    return 'Rayons';
  };

  const filteredRayons = rayons.filter(r => 
    r.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getIcon = (slug) => {
    const Icon = RAYON_ICONS[slug] || Wrench;
    return Icon;
  };

  const getColor = (slug) => {
    return RAYON_COLORS[slug] || '#F97316';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 md:left-[288px] z-[9999] bg-black/50"
        style={sidebarWidth !== undefined ? { left: sidebarWidth } : {}}
        onClick={onClose}
      >
        {/* Panel menu */}
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          exit={{ x: '-100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed inset-y-0 left-0 md:left-[288px] w-full max-w-md bg-white shadow-2xl overflow-hidden flex flex-col"
          style={sidebarWidth !== undefined ? { left: sidebarWidth } : {}}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-[#F97316] to-[#EA580C]">
            <div className="flex items-center gap-3">
              {(selectedRayon || selectedSousRayon) ? (
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-white/20 rounded-lg text-white"
                >
                  <ChevronLeft size={20} />
                </button>
              ) : (
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Menu size={20} className="text-white" />
                </div>
              )}
              <h2 className="text-lg font-bold text-white">
                {getHeaderTitle()}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Search */}
          {!selectedRayon && (
            <div className="p-4 border-b">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un rayon..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F97316]/20"
                />
              </div>
            </div>
          )}

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto">
            <AnimatePresence mode="wait">
              
              {/* ═══ PANEL 1: LISTE DES RAYONS ═══ */}
              {!selectedRayon && !selectedSousRayon && (
                <motion.div
                  key="rayons"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  {/* Trouver le bon produit */}
                  <Link
                    to="/produits"
                    onClick={onClose}
                    className="flex items-center gap-4 p-4 border-b border-gray-100 hover:bg-orange-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center">
                      <Sparkles size={20} className="text-white" />
                    </div>
                    <span className="flex-1 font-semibold text-gray-900">Trouver le bon produit</span>
                    <ChevronRight size={20} className="text-[#F97316]" />
                  </Link>

                  {/* Titre */}
                  <div className="px-4 py-3 bg-gray-50">
                    <h3 className="font-bold text-gray-900">Rayons</h3>
                  </div>

                  {/* Liste rayons */}
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-3 border-[#F97316]/30 border-t-[#F97316] rounded-full animate-spin" />
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {filteredRayons.map((rayon, idx) => {
                        const Icon = getIcon(rayon.slug);
                        const color = getColor(rayon.slug);
                        
                        return (
                          <motion.button
                            key={rayon.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.02 }}
                            onClick={() => handleRayonClick(rayon)}
                            className="flex items-center gap-4 p-4 w-full text-left hover:bg-gray-50 transition-colors"
                          >
                            <div 
                              className="w-10 h-10 rounded-xl flex items-center justify-center"
                              style={{ backgroundColor: `${color}15` }}
                            >
                              <Icon size={20} style={{ color }} />
                            </div>
                            <span className="flex-1 font-medium text-gray-900">{rayon.name}</span>
                            <ChevronRight size={20} className="text-gray-400" />
                          </motion.button>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}

              {/* ═══ PANEL 2: LISTE DES SOUS-RAYONS ═══ */}
              {selectedRayon && !selectedSousRayon && (
                <motion.div
                  key="sous-rayons"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  {/* Voir tous les produits du rayon */}
                  <Link
                    to={`/produits?rayon=${selectedRayon.slug}`}
                    onClick={onClose}
                    className="flex items-center gap-4 p-4 bg-orange-50 hover:bg-orange-100 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#F97316] flex items-center justify-center">
                      {(() => {
                        const Icon = getIcon(selectedRayon.slug);
                        return <Icon size={22} className="text-white" />;
                      })()}
                    </div>
                    <div className="flex-1">
                      <span className="font-bold text-[#F97316]">Tous les produits</span>
                      <p className="text-sm text-gray-500">{selectedRayon.name}</p>
                    </div>
                    <ChevronRight size={20} className="text-[#F97316]" />
                  </Link>

                  {/* Liste des sous-rayons (texte simple) */}
                  <div className="divide-y divide-gray-100">
                    {selectedRayon.sous_rayons && selectedRayon.sous_rayons.map((sousRayon, idx) => (
                      <motion.button
                        key={sousRayon.id || idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.02 }}
                        onClick={() => handleSousRayonClick(sousRayon)}
                        className="flex items-center justify-between w-full p-4 hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-medium text-gray-900">{sousRayon.name}</span>
                        <ChevronRight size={18} className="text-gray-400" />
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ═══ PANEL 3: CATÉGORIES EN LISTE ═══ */}
              {selectedSousRayon && (
                <motion.div
                  key="categories"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  {/* Voir tous les produits du sous-rayon */}
                  <Link
                    to={`/produits?sous_rayon=${selectedSousRayon.slug}`}
                    onClick={onClose}
                    className="flex items-center gap-4 p-4 bg-orange-50 hover:bg-orange-100 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#F97316] flex items-center justify-center">
                      {(() => {
                        const Icon = selectedRayon ? getIcon(selectedRayon.slug) : Wrench;
                        return <Icon size={22} className="text-white" />;
                      })()}
                    </div>
                    <div className="flex-1">
                      <span className="font-bold text-[#F97316]">Tous les produits</span>
                      <p className="text-sm text-gray-500">{selectedSousRayon.name}</p>
                    </div>
                    <ChevronRight size={20} className="text-[#F97316]" />
                  </Link>

                  {/* Liste des catégories (texte simple comme les autres panels) */}
                  <div className="divide-y divide-gray-100">
                    {selectedSousRayon.categories && selectedSousRayon.categories.map((cat, catIdx) => (
                      <motion.button
                        key={cat.id || catIdx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: catIdx * 0.02 }}
                        onClick={() => handleCategoryClick(cat)}
                        className="flex items-center justify-between w-full p-4 hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-medium text-gray-900">{cat.name}</span>
                        <ChevronRight size={18} className="text-gray-400" />
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
              
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50">
            <p className="text-xs text-center text-gray-500">
              AbracadaBati • Le centre commercial virtuel du bâtiment
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Bouton pour ouvrir le menu catalogue (à utiliser dans le header ou la nav)
export function CatalogueMenuButton({ onClick, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium rounded-xl transition-colors ${className}`}
      data-testid="btn-catalogue-menu"
    >
      <Menu size={18} />
      <span>Rayons</span>
    </button>
  );
}
